'use client';

import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import Lottie from 'lottie-react';
import Image from 'next/image';
import text from 'public/assets/lottie/text-ellipse.json';
import { FC, useEffect, useRef } from 'react';
import { HeroDto } from '@/components/Hero/types';
import Loader from '@/components/Loader';
import VideoPreview from '@/components/VideoPreview';
import { useAboutUsVideo } from '@/contexts/AboutUsVideoContext';
import useDevice from '@/hooks/useDevice';
import useLoader from '@/hooks/useLoader';
import useLottieHandlers from '@/hooks/useLottieHandlers';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import CustomSlider from './CustomSlider';
import { useHeroFlashImgRef } from './hooks/useHeroFlashImgRef';
import { useHeroFlashIntro } from './hooks/useHeroFlashIntro';
import { useHeroMediaReady } from './hooks/useHeroMediaReady';
import { useHeroParallax } from './hooks/useHeroParallax';
import { useHeroPinScroll } from './hooks/useHeroPinScroll';
import { useHeroRevealAnimations } from './hooks/useHeroRevealAnimations';
import css from './style.module.scss';
interface Props {
  data: HeroDto;
}
const Hero: FC<Props> = t0 => {
  const $ = _c(64);
  const {
    data
  } = t0;
  useLoader();
  const device = useDevice();
  const aboutUsVideo = useAboutUsVideo();
  const openAboutUsVideo = aboutUsVideo?.openAboutUsVideo;
  const {
    setSpeedHandler,
    lottieRef: textLottieRef,
    setSpeedHandler: setSpeedTextHandler
  } = useLottieHandlers();
  const wrapperRef = useRef(null);
  const gradientRef = useRef(null);
  const graffitiRef = useRef(null);
  const loaderRef = useRef(null);
  const {
    flashRef,
    setFlashRef,
    flashMountKey
  } = useHeroFlashImgRef();
  let t1;
  if ($[0] !== flashMountKey || $[1] !== flashRef) {
    t1 = {
      wrapperRef,
      loaderRef,
      flashRef,
      flashMountKey
    };
    $[0] = flashMountKey;
    $[1] = flashRef;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isHeroReady = useHeroMediaReady(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [wrapperRef];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useViewportVideosPlayback(t2);
  useHeroPinScroll(wrapperRef);
  let t3;
  if ($[4] !== isHeroReady) {
    t3 = {
      wrapperRef,
      loaderRef,
      isPageReady: isHeroReady
    };
    $[4] = isHeroReady;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useHeroRevealAnimations(t3);
  let t4;
  if ($[6] !== flashMountKey || $[7] !== flashRef || $[8] !== isHeroReady) {
    t4 = {
      wrapperRef,
      flashRef,
      isPageReady: isHeroReady,
      flashMountKey
    };
    $[6] = flashMountKey;
    $[7] = flashRef;
    $[8] = isHeroReady;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useHeroFlashIntro(t4);
  let t5;
  if ($[10] !== flashRef || $[11] !== isHeroReady) {
    t5 = {
      wrapperRef,
      gradientRef,
      graffitiRef,
      flashRef,
      isPageReady: isHeroReady
    };
    $[10] = flashRef;
    $[11] = isHeroReady;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useHeroParallax(t5);
  let t6;
  if ($[13] !== openAboutUsVideo) {
    t6 = () => openAboutUsVideo?.();
    $[13] = openAboutUsVideo;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const openAboutUsVideoHandler = t6;
  let t7;
  let t8;
  if ($[15] !== setSpeedHandler || $[16] !== setSpeedTextHandler) {
    t7 = () => {
      setSpeedHandler(0.7);
      setSpeedTextHandler(0.3);
    };
    t8 = [setSpeedHandler, setSpeedTextHandler];
    $[15] = setSpeedHandler;
    $[16] = setSpeedTextHandler;
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  useEffect(t7, t8);
  let t10;
  let t11;
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = clsx("wrapper", css.wrapper);
    t10 = <Loader ref={loaderRef} />;
    t11 = <div ref={gradientRef} className={css.gradientLayer} />;
    $[19] = t10;
    $[20] = t11;
    $[21] = t9;
  } else {
    t10 = $[19];
    t11 = $[20];
    t9 = $[21];
  }
  let t12;
  if ($[22] !== setFlashRef) {
    t12 = <Image ref={setFlashRef} preload={true} className={css.flashBg} width={500} height={500} src="/assets/images/hero/flash-bg.svg" alt="flash background" />;
    $[22] = setFlashRef;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className={css.parallaxContainer}><div ref={graffitiRef} className={css.parallax}><video playsInline={true} muted={true} autoPlay={true} className={css.graffitiBg} preload="metadata"><source src="/assets/video/mov/loader_2.mov" type={"video/quicktime; codecs=\"hvc1\""} /><source src="/assets/video/webm/loader.webm" type="video/webm" /></video></div></div>;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== openAboutUsVideoHandler) {
    t14 = <div className={css.videoChubClickHandler} onClick={openAboutUsVideoHandler} />;
    $[25] = openAboutUsVideoHandler;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== textLottieRef) {
    t15 = <Lottie loop={true} autoplay={true} lottieRef={textLottieRef} className={css.lottieContainer} animationData={text} />;
    $[27] = textLottieRef;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <video loop={true} muted={true} autoPlay={true} playsInline={true} data-hero-media="chub" className={css.videoChub} controls={false} preload="metadata"><source src="/assets/video/mov/hero-chub.mov" type={"video/quicktime; codecs=\"hvc1\""} /><source src="/assets/video/webm/hero-chub.webm" type="video/webm" /></video>;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t14 || $[31] !== t15) {
    t17 = <div className={css.videoWrapper}>{t14}{t15}{t16}</div>;
    $[30] = t14;
    $[31] = t15;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== data.sliderTitle) {
    t18 = <p className={css.sliderTitle}>{data.sliderTitle}</p>;
    $[33] = data.sliderTitle;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <img src="/assets/icons/clutch-logo.svg" style={{
      marginBottom: "1px"
    }} height={12} width={42} fetchPriority="low" alt="Clutch logo" />;
    t20 = <span>5.0</span>;
    t21 = <div className={css.verticalDivider} />;
    t22 = <img src="/assets/icons/stars-rating.svg" height={11} width={65} fetchPriority="low" alt="" />;
    $[35] = t19;
    $[36] = t20;
    $[37] = t21;
    $[38] = t22;
  } else {
    t19 = $[35];
    t20 = $[36];
    t21 = $[37];
    t22 = $[38];
  }
  let t23;
  if ($[39] !== data.clutchReviewsLabel) {
    t23 = <span className={css.clutchReviewsLabel}>{data.clutchReviewsLabel}</span>;
    $[39] = data.clutchReviewsLabel;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== data.clutchLink || $[42] !== t23) {
    t24 = <div className={css.clutchInfo}><a className={css.clutchLink} href={data.clutchLink} target="_blank" rel="noopener noreferrer">{t19}{t20}{t21}{t22}{t23}</a></div>;
    $[41] = data.clutchLink;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t18 || $[45] !== t24) {
    t25 = <div className={css.clutchWrapper}>{t18}{t24}</div>;
    $[44] = t18;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== data.newSlider) {
    t26 = data?.newSlider || [];
    $[47] = data.newSlider;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== t26) {
    t27 = <div className={css.sliderWrapper}><CustomSlider items={t26} /></div>;
    $[49] = t26;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== data.videoLink || $[52] !== data.videoThumbnailUrl || $[53] !== device || $[54] !== openAboutUsVideoHandler) {
    t28 = device === "desktop" && <div className={css.videoContainer}><VideoPreview thumbnailLoading="eager" videoUrl={data.videoLink} thumbnailUrl={data.videoThumbnailUrl} onClick={openAboutUsVideoHandler} /></div>;
    $[51] = data.videoLink;
    $[52] = data.videoThumbnailUrl;
    $[53] = device;
    $[54] = openAboutUsVideoHandler;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== t25 || $[57] !== t27 || $[58] !== t28) {
    t29 = <div className={css.bottomContainer}>{t25}{t27}{t28}</div>;
    $[56] = t25;
    $[57] = t27;
    $[58] = t28;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== t12 || $[61] !== t17 || $[62] !== t29) {
    t30 = <section ref={wrapperRef} className={t9}>{t10}{t11}{t12}{t13}{t17}{t29}</section>;
    $[60] = t12;
    $[61] = t17;
    $[62] = t29;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  return t30;
};
export default Hero;